import axios from 'axios';
import { CONFIG } from '../config/constants';
import { UserRole } from '../config/roles';

// Define API base URL from constants
const API_URL = CONFIG.API.API_URL;

// Define types
export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: {
    id: number;
    name: string;
    email: string;
    role: UserRole;
    avatar?: string;
    phone?: string;
    birth_date?: string;
    bio?: string;
    job_title?: string;
    company?: string;
    location?: string;
    website?: string;
    preferences?: {
      notifications?: boolean;
      email_notifications?: boolean;
      theme?: 'light' | 'dark' | 'auto';
      language?: string;
      timezone?: string;
    };
    email_verified_at?: string;
    created_at: string;
    updated_at: string;
  };
  message: string;
  token?: string;
}

// Configure axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add a request interceptor to include the token in requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Authentication service
const authService = {
  // Register a new user
  register: async (data: RegisterData): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post('/register', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Registration failed');
    }
  },

  // Login user
  login: async (data: LoginData): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post('/login', data);
      
      // Store token in localStorage if provided
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Login failed');
    }
  },

  // Logout user
  logout: async (): Promise<void> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        // Call the logout API endpoint
        await apiClient.post('/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always remove the token from localStorage
      localStorage.removeItem('auth_token');
    }
  },

  // Get current authenticated user
  getCurrentUser: (): { token: string | null } => {
    const token = localStorage.getItem('auth_token');
    return { token };
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return localStorage.getItem('auth_token') !== null;
  },
};

export default authService; 